import React from 'react';
import { API } from '../api';
import { Field, Section } from './ui';
import { occurrencesBetween, seriesEnd, repeatLabel } from '../utils/eventRecurrence';

/*
 * The season sheet: the club's year on one page.
 *
 * The club noticeboard version of this is a spreadsheet somebody retypes
 * every August — competitions, then markets, then the dates everyone asks
 * about — and every year it disagrees with the app by a week somewhere.
 * This draws it from the records the app already holds, so the sheet and
 * the app cannot disagree.
 *
 * Drawn here on a canvas rather than generated by the image model, which is
 * the whole point. Twenty-five rows of names, dates and notes have to be
 * exactly right — a member reads this and turns up on the day it says — and
 * an image model will quietly misspell a date, drop a row, or invent a
 * competition that does not exist. It also costs nothing and takes no time,
 * so an admin can adjust a note and redraw it as often as they like.
 *
 * The finished picture is posted to /api/posters/compose and becomes an
 * ordinary poster: it can go on the club screen, into a newsletter, or to
 * the printer, and it is deleted the same way as any other.
 */

/* A4 at 200dpi. Portrait, because that is what goes on a noticeboard and
   into a printer without anybody thinking about it. */
const SHEET_W = 1654;
const SHEET_H = 2339;

const dayOnly = v => (v ? String(v).slice(0, 10) : '');

/* A club colour at a given transparency. The palette is stored as hex, and
   the row banding wants the club's own green — or maroon, or navy — rather
   than a tint of somebody else's. */
function rgba(hex, alpha) {
  const m = String(hex || '').trim().replace('#', '');
  const full = m.length === 3 ? m.split('').map(c => c + c).join('') : m;
  if (!/^[0-9a-f]{6}$/i.test(full)) return 'rgba(0,0,0,' + alpha + ')';
  const n = parseInt(full, 16);
  return 'rgba(' + ((n >> 16) & 255) + ',' + ((n >> 8) & 255) + ',' + (n & 255) + ',' + alpha + ')';
}

const asObject = raw => {
  let v = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { return {}; } }
  return (v && typeof v === 'object') ? v : {};
};

const D = iso => {
  const d = new Date(dayOnly(iso) + 'T00:00:00');
  return Number.isNaN(d.getTime()) ? null : d;
};

/* "Sun 27 Sept" — the club's own shorthand, not a machine's. The comma
   en-NZ puts after the weekday is dropped: a noticeboard writes "Sun 27
   Sept", and the comma is one more mark to read at three metres. */
const shortDate = iso => {
  const d = D(iso);
  if (!d) return '';
  return d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' })
    .replace(',', '');
};

const dayAndMonth = iso => {
  const d = D(iso);
  return d ? d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' }) : '';
};

/** "6pm" from "18:00", left alone if the club already wrote it in words. */
function timeWord(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  const m = t.match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return t;
  const h = Number(m[1]);
  const mins = m[2] === '00' ? '' : '.' + m[2];
  const suffix = h < 12 ? 'am' : 'pm';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}${mins}${suffix}`;
}

/**
 * When a competition is played, in the words the club's own sheet uses.
 *
 * The rounds are what a competition actually is — "Wed 6pm 25 Nov to 20 Jan"
 * — so the dates come from the round dates where there are any, and fall
 * back to the start-to-end span where there are not. A competition on a
 * single day says that day and nothing else.
 */
export function competitionWhen(comp) {
  const rounds = Object.entries(asObject(comp.roundStartDates))
    .filter(([n]) => !Number(comp.numberOfRounds) || Number(n) <= Number(comp.numberOfRounds))
    .map(([n, date]) => ({ n: Number(n), date: dayOnly(date) }))
    .filter(r => r.date)
    .sort((a, b) => a.n - b.n);

  const finals = Object.values(asObject(comp.finalsStartDates)).map(dayOnly).filter(Boolean).sort();
  const days = [...rounds.map(r => r.date), ...finals].sort();
  const first = days[0] || dayOnly(comp.startDate);
  const last = days[days.length - 1] || dayOnly(comp.endDate) || first;
  if (!first) return 'To be confirmed';

  const time = timeWord(asObject(comp.roundStartTimes)[String(rounds[0] ? rounds[0].n : 1)]);
  const startD = D(first);
  /* A weekly competition is "Wed 6pm 25 Nov to 20 Jan", not thirteen dates.
     The weekday carries more than the span does: it is the thing a member
     has to keep free. */
  const weekday = startD ? startD.toLocaleDateString('en-NZ', { weekday: 'short' }) : '';
  const head = [weekday, time].filter(Boolean).join(' ');

  if (!last || last === first) return [head, dayAndMonth(first)].filter(Boolean).join(' ');
  return `${[head, dayAndMonth(first)].filter(Boolean).join(' ')} to ${dayAndMonth(last)}`;
}

/** When an event is on. A series says it is one. */
export function eventWhen(ev) {
  if (ev.repeat) {
    const next = occurrencesBetween(ev, dayOnly(ev.startDate), seriesEnd(ev))[0];
    return [repeatLabel(ev), next ? `from ${dayAndMonth(next)}` : ''].filter(Boolean).join(', ');
  }
  const start = dayOnly(ev.startDate);
  if (!start) return 'To be confirmed';
  const time = timeWord(ev.startTime);
  const end = dayOnly(ev.endDate);
  const base = [time, shortDate(start)].filter(Boolean).join(' ');
  return end && end !== start ? `${base} to ${dayAndMonth(end)}` : base;
}

/*
 * What the club already knows about a competition, in the words the note
 * column wants. Each is a separate answer so the sheet can carry the two a
 * club cares about rather than all six: "4 games, 2 bowls, $10" reads; the
 * whole record does not.
 */
const FORMAT_WORDS = {
  singles: 'Singles', pairs: 'Pairs', triples: 'Triples', fours: 'Fours',
  '2-4-2-pairs': '2-4-2 pairs',
};

export const NOTE_FIELDS = [
  ['format', 'Format', c => FORMAT_WORDS[c.format] || (c.format ? String(c.format) : '')],
  ['draw', 'Draw', c => (c.tournamentType === 'round-robin' ? 'Round robin'
    : c.tournamentType === 'knockout' ? 'Knockout'
      : c.tournamentType === 'double-elimination' ? 'Double elimination'
        : c.tournamentType === 'swiss' ? 'Swiss' : '')],
  ['rounds', 'Games', c => (Number(c.numberOfRounds) ? `${c.numberOfRounds} games` : '')],
  ['ends', 'Ends', c => (c.matchType === 'ends' && Number(c.numberOfEnds) ? `${c.numberOfEnds} ends`
    : c.matchType === 'first-to' && Number(c.targetScore) ? `first to ${c.targetScore}`
      : c.matchType === 'timed' && Number(c.timeLimit) ? `${c.timeLimit} min` : '')],
  ['bowls', 'Bowls', c => (Number(c.bowlsPerPlayer) ? `${c.bowlsPerPlayer} bowls` : '')],
  ['fee', 'Entry fee', c => (Number(c.entryFee) > 0
    ? '$' + Number(c.entryFee).toFixed(2).replace(/\.00$/, '') : 'Free')],
  ['finals', 'Finals date', c => {
    const days = Object.values(asObject(c.finalsStartDates)).map(dayOnly).filter(Boolean).sort();
    return days.length ? 'finals ' + dayAndMonth(days[days.length - 1]) : '';
  }],
];

/** The note a competition writes for itself, from the fields ticked. */
export function autoNote(comp, include) {
  return NOTE_FIELDS
    .filter(([key]) => include[key])
    .map(([, , read]) => read(comp))
    .filter(Boolean)
    .join(' · ');
}

/**
 * The sheet's rows, grouped the way a club groups them.
 *
 * Competitions first, because that is what the year is built around. Then
 * the events, gathered by their kind — a club that types "Market Day" on
 * five events gets a Markets section without having asked for one, which is
 * exactly how the sheet on the noticeboard is laid out.
 */
export function sheetSections({ comps, events, compIds, eventIds, include = {} }) {
  const has = (ids, id) => (ids || []).some(x => String(x) === String(id));
  const pickedComps = (comps || []).filter(c => has(compIds, c.id));
  const pickedEvents = (events || []).filter(e => has(eventIds, e.id));

  const sections = [];
  if (pickedComps.length) {
    sections.push({
      title: 'Club Competitions',
      rows: pickedComps.map(c => ({
        id: 'c' + c.id,
        name: c.name,
        when: competitionWhen(c),
        /* Written from the competition's own settings, so a club that has
           already told the app it is 4 games of 2 bowls at $10 does not
           type that again on a sheet. */
        note: autoNote(c, include),
        sort: dayOnly(c.startDate) || '9999',
      })),
    });
  }

  const byKind = new Map();
  pickedEvents.forEach(e => {
    /* The club's own word for it, pluralised only where it obviously wants
       to be. "Market Day" becomes "Market Days"; anything already plural is
       left alone, because "Marketss" is worse than an odd heading. */
    const kind = (e.kind || '').trim();
    const heading = kind ? (/s$/i.test(kind) ? kind : kind + 's') : 'Club Events';
    if (!byKind.has(heading)) byKind.set(heading, []);
    byKind.get(heading).push({
      id: 'e' + e.id,
      name: e.name,
      when: eventWhen(e),
      /* An event has no format or bowls to report, so its note is what the
         club wrote about it: the cost and where it is if those are ticked,
         then its own words. */
      note: [
        include.fee ? (e.cost || '').trim() : '',
        include.where ? (e.location || '').trim() : '',
        (e.details || '').trim(),
      ].filter(Boolean).join(' · '),
      sort: dayOnly(e.nextDate || e.startDate) || '9999',
    });
  });

  [...byKind.entries()].forEach(([title, rows]) => sections.push({ title, rows }));

  sections.forEach(s => s.rows.sort((a, b) => (a.sort < b.sort ? -1 : a.sort > b.sort ? 1 : 0)));
  return sections;
}

/* Wrapping done here rather than by the canvas, which has no idea what a
   line is. Measured word by word against the real font, so a long note in a
   narrow column breaks where it would break on paper. */
function wrap(ctx, text, maxWidth, maxLines) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach(w => {
    const attempt = line ? line + ' ' + w : w;
    if (ctx.measureText(attempt).width <= maxWidth || !line) { line = attempt; return; }
    lines.push(line);
    line = w;
  });
  if (line) lines.push(line);
  if (!maxLines || lines.length <= maxLines) return lines;
  const kept = lines.slice(0, maxLines);
  kept[maxLines - 1] = kept[maxLines - 1].replace(/[,.;:]?$/, '') + '…';
  return kept;
}

/**
 * One line of text made to fit its column.
 *
 * Steps the size down until the words fit, and only gives up — with an
 * ellipsis — when a name is longer than two thirds of the type it started
 * at could carry. Nothing on a noticeboard should be silently cut off, and
 * a club that names a competition "2026 Championship Singles" has said
 * something it needs read.
 */
function fitText(ctx, text, x, y, maxWidth, size, fontTemplate) {
  const str = String(text || '');
  if (!str) return;
  const font = s => fontTemplate.replace('%SIZE%', String(s));
  let px = size;
  ctx.font = font(px);
  while (ctx.measureText(str).width > maxWidth && px > Math.round(size * 0.66)) {
    px -= 1;
    ctx.font = font(px);
  }
  if (ctx.measureText(str).width <= maxWidth) { ctx.fillText(str, x, y); return; }
  let cut = str;
  while (cut.length > 1 && ctx.measureText(cut + '…').width > maxWidth) cut = cut.slice(0, -1);
  ctx.fillText(cut + '…', x, y);
}

/**
 * Draw the sheet.
 *
 * Everything is measured from the sheet width, so the same code draws a
 * legible A4 at 200dpi and a legible preview at 300px. Rows are laid out in
 * one pass and the type shrinks by a step if the year does not fit — a sheet
 * that runs off the bottom of the page is not a sheet.
 */
export function drawSeasonSheet(canvas, { theme, sections, title, footNote, logo, backdrop }) {
  const ctx = canvas.getContext('2d');
  canvas.width = SHEET_W;
  canvas.height = SHEET_H;

  /* Every colour on the sheet is the club's, from the same palette the app
     and the club screen use. The fallbacks are GreenSheet's own and apply
     only to a club that has never set a theme — a hard-coded green on a club
     whose colours are maroon is somebody else's sheet. */
  const c60 = (theme && theme.c60) || '#04490c';
  const c30 = (theme && theme.c30) || '#069f1a';
  /* Body text is ink, not brand: a table read at three metres needs
     contrast, and a pale club colour set as body text is unreadable however
     correct it is. The club's colours carry the masthead, the rules, the
     dates and the banding, which is where they are seen. */
  const ink = '#16281a';
  const muted = '#5c6b60';
  const band = rgba(c30, 0.06);

  const M = Math.round(SHEET_W * 0.055);          // page margin
  const bandH = Math.round(SHEET_H * 0.062);

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, SHEET_W, SHEET_H);

  /* The artwork, if the club has asked for one: drawn to fill the page, then
     a panel laid over the middle so the table reads as a table rather than
     as words floating on a picture. The rows are drawn on top of that. This
     is the whole trick — the model decorates the page, the app writes on
     it, and nothing the model does can alter a date. */
  if (backdrop) {
    try {
      const scale = Math.max(SHEET_W / backdrop.width, SHEET_H / backdrop.height);
      const w = backdrop.width * scale;
      const h = backdrop.height * scale;
      ctx.drawImage(backdrop, (SHEET_W - w) / 2, (SHEET_H - h) / 2, w, h);
    } catch { /* a backdrop that will not draw is not worth losing the sheet over */ }
  }

  /* Over artwork the table needs its own ground, or the notes in grey sit on
     a photograph and stop being readable at arm's length. Slightly inset and
     rounded, so it reads as a card on the page rather than a patch. */
  if (backdrop) {
    const inset = Math.round(M * 0.45);
    const top = bandH - inset;
    const r = Math.round(SHEET_W * 0.012);
    const x = inset;
    const y0 = top;
    const w = SHEET_W - inset * 2;
    const h = SHEET_H - inset - top;
    ctx.beginPath();
    ctx.moveTo(x + r, y0);
    ctx.arcTo(x + w, y0, x + w, y0 + h, r);
    ctx.arcTo(x + w, y0 + h, x, y0 + h, r);
    ctx.arcTo(x, y0 + h, x, y0, r);
    ctx.arcTo(x, y0, x + w, y0, r);
    ctx.closePath();
    ctx.fillStyle = 'rgba(255,255,255,0.93)';
    ctx.fill();
  }

  /* The masthead: club colour edge to edge, the title in it, the logo at the
     left. The same band the club's own sheet has. */
  ctx.fillStyle = c60;
  ctx.fillRect(0, 0, SHEET_W, bandH);
  if (logo) {
    const size = Math.round(bandH * 0.78);
    const pad = Math.round((bandH - size) / 2);
    try { ctx.drawImage(logo, pad, pad, size, size); } catch { /* a bad logo is not worth losing the sheet over */ }
  }
  ctx.fillStyle = '#ffffff';
  ctx.textBaseline = 'middle';
  ctx.textAlign = 'center';
  ctx.font = `700 ${Math.round(bandH * 0.46)}px Georgia, "Times New Roman", serif`;
  ctx.fillText(title, SHEET_W / 2, bandH / 2);
  ctx.textAlign = 'left';

  const rowCount = sections.reduce((n, s) => n + s.rows.length, 0);
  const space = SHEET_H - bandH - M * 2 - (footNote ? Math.round(SHEET_H * 0.03) : 0);
  const needed = sections.length * 2.4 + rowCount;
  /* One pass of fitting rather than a loop: the row height is whatever
     divides the space, bounded so a five-row sheet is not drawn in headlines
     and a fifty-row one is still readable. */
  const rowH = Math.max(
    Math.round(SHEET_H * 0.017),
    Math.min(Math.round(SHEET_H * 0.032), Math.floor(space / Math.max(1, needed))),
  );
  const nameSize = Math.round(rowH * 0.5);
  const whenSize = Math.round(rowH * 0.42);
  const noteSize = Math.round(rowH * 0.34);

  /* The columns are measured from the words that have to go in them, not
     fixed at a third each. "2026 Championship Singles" was being cut to
     "2026 Championship…" against a column that happened to be 36% wide,
     which is the one thing a sheet like this must never do: a name nobody
     can read is worse than no row at all.

     So each column is asked how wide its widest entry is, and the three are
     fitted to the page in order of how badly they need the room — the name
     first, then the date, and the note takes what is left. */
  const gap = Math.round(SHEET_W * 0.02);
  const usable = SHEET_W - M * 2;
  const widest = (rows, font, read) => {
    ctx.font = font;
    return rows.reduce((w, r) => Math.max(w, ctx.measureText(read(r) || '').width), 0);
  };
  const allRows = sections.reduce((acc, s) => acc.concat(s.rows), []);
  const nameFont = `600 ${nameSize}px Georgia, "Times New Roman", serif`;
  const whenFont = `${whenSize}px Georgia, "Times New Roman", serif`;

  const anyNote = allRows.some(r => r.note);
  const wantName = widest(allRows, nameFont, r => r.name);
  const wantWhen = widest(allRows, whenFont, r => r.when);
  /* A note is squeezable — it is the one column whose job survives wrapping
     — so it is given a share only after the other two have what they need,
     and never more than a third of the page. */
  const noteShare = anyNote ? Math.round(usable * 0.3) : 0;
  let nameW = Math.min(wantName, usable * 0.5);
  let whenW = Math.min(wantWhen, usable * 0.34);
  const over = (nameW + whenW + noteShare + gap * 2) - usable;
  if (over > 0) {
    /* Both columns give up room in proportion to what they asked for, so a
       long name and a long date line shrink together rather than one of them
       being cut to nothing. */
    const total = nameW + whenW;
    nameW -= over * (nameW / total);
    whenW -= over * (whenW / total);
  }
  const colName = M;
  const colWhen = Math.round(colName + nameW + gap);
  const colNote = Math.round(colWhen + whenW + gap);
  const noteW = Math.max(0, SHEET_W - M - colNote);

  let y = bandH + M;

  sections.forEach(section => {
    /* A section heading centred over its rows, as on the club's sheet, with
       a rule under it so the eye knows where the group starts. */
    y += rowH * 0.6;
    ctx.fillStyle = ink;
    ctx.font = `700 ${Math.round(rowH * 0.62)}px Georgia, "Times New Roman", serif`;
    ctx.textAlign = 'center';
    ctx.fillText(section.title, SHEET_W / 2, y);
    ctx.textAlign = 'left';
    y += rowH * 0.45;
    ctx.strokeStyle = c30;
    ctx.lineWidth = Math.max(1, Math.round(SHEET_W * 0.0016));
    ctx.beginPath();
    ctx.moveTo(M, y);
    ctx.lineTo(SHEET_W - M, y);
    ctx.stroke();
    y += rowH * 0.85;

    section.rows.forEach((row, i) => {
      if (i % 2 === 1) {
        /* Banding rather than gridlines: a table of twenty-five rows read
           from a noticeboard needs the eye carried across, and lines drawn
           around every cell are what makes a printed spreadsheet hard to
           read at a distance. */
        ctx.fillStyle = band;
        ctx.fillRect(M - rowH * 0.25, y - rowH * 0.62, SHEET_W - (M - rowH * 0.25) * 2, rowH);
      }
      /* Shrunk to fit rather than cut off. A name a couple of characters too
         long for its column is set a point or two smaller and still says
         what it is; cutting it to "2026 Championship…" leaves a member
         unable to tell two competitions apart, which is the whole reason
         the row exists. */
      ctx.fillStyle = ink;
      fitText(ctx, row.name, colName, y - rowH * 0.08, nameW,
        nameSize, `600 %SIZE%px Georgia, "Times New Roman", serif`);

      ctx.fillStyle = c60;
      fitText(ctx, row.when, colWhen, y - rowH * 0.08, whenW,
        whenSize, `%SIZE%px Georgia, "Times New Roman", serif`);

      if (row.note) {
        ctx.fillStyle = muted;
        ctx.font = `${noteSize}px Georgia, "Times New Roman", serif`;
        const lines = wrap(ctx, row.note, noteW, 2);
        lines.forEach((line, li) => {
          ctx.fillText(line, colNote, y - rowH * 0.22 + li * noteSize * 1.25);
        });
      }
      y += rowH;
    });
  });

  if (footNote) {
    ctx.fillStyle = muted;
    ctx.font = `italic ${Math.round(rowH * 0.38)}px Georgia, "Times New Roman", serif`;
    ctx.textAlign = 'center';
    ctx.fillText(footNote, SHEET_W / 2, SHEET_H - M * 0.75);
    ctx.textAlign = 'left';
  }
  return canvas;
}

/** The club's logo as something drawImage will take, or null. */
function loadImage(src) {
  return new Promise(resolve => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/* The season a club is in, the way a club says it: "2026/2027" from July. */
function defaultSeasonTitle() {
  const now = new Date();
  const start = now.getMonth() >= 6 ? now.getFullYear() : now.getFullYear() - 1;
  return `${start}/${start + 1} Club Events`;
}

function SeasonSheet({ busy: parentBusy, canGenerate, clubId, compIds, config, eventIds, onGenerated, onMade, showToast }) {
  const comps = React.useMemo(() => (config.competitions || []), [config]);
  const events = React.useMemo(() => (config.events || []), [config]);

  const [title, setTitle] = React.useState(defaultSeasonTitle);
  const [footNote, setFootNote] = React.useState('Event dates may change depending on weather conditions.');
  /* What the sheet says about each row beyond its name and its date. The
     three a club's own sheet always carries are on; the rest are there for
     the club that wants them. */
  const [include, setInclude] = React.useState({
    format: true, rounds: true, fee: true, where: true,
    draw: false, ends: false, bowls: false, finals: false,
  });
  /* Notes are the club's own words about a row — "4 qualifying games, semi
     and final" — and there is nowhere else in the app they belong, so they
     live on the sheet and are typed here. */
  const [notes, setNotes] = React.useState({});
  /* Dates are worked out from the records, and are right almost always —
     but "almost" is the problem on a sheet somebody turns up to. A green
     closed for the week, a final moved, a competition the club runs to a
     rhythm the round dates do not describe: the club has to be able to say
     so here without going back and editing the competition itself. Same
     shape as notes: an override per row, the generated text until then. */
  const [dates, setDates] = React.useState({});
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');
  /* The artwork the model drew, once a club has asked for one. Held as a
     loaded image so every redraw is instant — a club adjusting a note after
     paying for a backdrop should not pay again, or wait. */
  const [backdrop, setBackdrop] = React.useState(null);
  const [drawing, setDrawing] = React.useState(false);
  const canvasRef = React.useRef(null);
  const previewRef = React.useRef(null);

  const sections = React.useMemo(() => {
    const built = sheetSections({ comps, events, compIds, eventIds, include });
    /* The generated text is kept alongside the edited one so a row can say
       what it would have said, and be put back to it. */
    return built.map(s => ({
      ...s,
      rows: s.rows.map(r => ({
        ...r,
        generatedWhen: r.when,
        when: dates[r.id] !== undefined ? dates[r.id] : r.when,
        note: notes[r.id] !== undefined ? notes[r.id] : r.note,
      })),
    }));
  }, [comps, events, compIds, eventIds, include, notes, dates]);

  const editedDateCount = sections.reduce(
    (n, s) => n + s.rows.filter(r => dates[r.id] !== undefined && dates[r.id] !== r.generatedWhen).length,
    0,
  );

  const rowCount = sections.reduce((n, s) => n + s.rows.length, 0);

  /* Redrawn on every change, because it costs nothing and a sheet you cannot
     see before you keep it is a sheet you keep twice. */
  /* The club's default logo, chosen the same way every poster chooses it:
     the one set as the club's default in the poster settings, then the
     club's own logo, then the first mark in its gallery. Not a picture
     picked here — a club has one logo and should not be asked twice. */
  const logoSrc = React.useMemo(() => {
    const logos = config.logos || [];
    const chosen = logos.find(l => String(l.id) === String(config.settings.logoId || ''));
    return (chosen && chosen.data) || config.defaultLogo || (logos[0] && logos[0].data) || null;
  }, [config.logos, config.defaultLogo, config.settings.logoId]);

  React.useEffect(() => {
    let cancelled = false;
    loadImage(logoSrc).then(logo => {
      if (cancelled || !canvasRef.current) return;
      drawSeasonSheet(canvasRef.current, { theme: config.theme, sections, title, footNote, logo, backdrop });
      if (previewRef.current) previewRef.current.src = canvasRef.current.toDataURL('image/jpeg', 0.9);
    });
    return () => { cancelled = true; };
  }, [config.theme, logoSrc, sections, title, footNote, backdrop]);

  const toggleField = key => setInclude(prev => ({ ...prev, [key]: !prev[key] }));

  /* 0.86 rather than 0.92: the difference is invisible on a page of type and
     it keeps the request comfortably inside what the API accepts. */
  const sheetImage = () => canvasRef.current.toDataURL('image/jpeg', 0.86);

  const save = async () => {
    setError('');
    setBusy(true);
    try {
      const imageData = sheetImage();
      const res = await fetch(`${API}/api/posters/compose`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clubId, title, subtitle: `${rowCount} rows`, kind: 'season', imageData }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        /* Say what actually came back. "Could not keep that" on its own sent
           somebody looking at the sheet when the answer was that the server
           had not been updated, or that the club's poster storage was full. */
        setError(data.error
          || (res.status === 404
            ? 'The server does not have the season sheet yet — it needs the latest version deployed.'
            : `Could not keep that (${res.status} ${res.statusText || 'error'}).`));
        return;
      }
      onMade(data);
      showToast('Season sheet saved with your posters.');
    } catch (e) {
      setError(e.message || 'Could not keep that.');
    } finally {
      setBusy(false);
    }
  };

  /* Printing goes straight from the canvas rather than through the poster
     gallery: the sheet is A4 and the reason a club makes one is to pin it
     up, which should not require saving it first. */
  const print = () => {
    const w = window.open('', '_blank');
    if (!w) { setError('Your browser blocked the print window. Allow pop-ups for this site.'); return; }
    w.document.write(
      `<!doctype html><title>${title}</title>`
      + '<style>@page{size:A4 portrait;margin:0}'
      + 'html,body{margin:0;padding:0}img{width:100%;height:auto;display:block}</style>'
      + `<img src="${sheetImage()}" alt="">`,
    );
    w.document.close();
    /* Waiting for the image to decode: printing a page whose only content
       has not loaded prints a blank sheet. */
    w.onload = () => { w.focus(); w.print(); };
  };

  /* "Make it look better" — artwork for the page, not a redraw of it. The
     model never sees the text and never gets a chance to retype a date; it
     draws the ground and the sheet is written over the top. */
  const jazz = async () => {
    setError('');
    setDrawing(true);
    try {
      const res = await fetch(`${API}/api/posters/backdrop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clubId, size: '1024x1536' }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(data.error
          || (res.status === 404
            ? 'The server does not have this yet — it needs the latest version deployed.'
            : `Could not draw that (${res.status} ${res.statusText || 'error'}).`));
        return;
      }
      const img = await loadImage(data.imageData);
      if (!img) { setError('The artwork came back but would not load.'); return; }
      setBackdrop(img);
      if (onGenerated) onGenerated(data);
      showToast('Artwork drawn. Every date on the sheet is still your own.');
    } catch (e) {
      setError(e.message || 'Could not draw that.');
    } finally {
      setDrawing(false);
    }
  };

  const download = () => {
    const a = document.createElement('a');
    a.href = sheetImage();
    a.download = `${title.replace(/[^\w -]+/g, '').trim() || 'club-events'}.jpg`;
    a.click();
  };

  return (
    <>
      <Section label="What the sheet says about each row"
        action={
          <span style={{ fontSize: 'var(--gs-text-sm)', color: 'var(--gs-text-muted)' }}>
            {rowCount ? `${rowCount} row${rowCount === 1 ? '' : 's'}` : 'Nothing picked'}
          </span>
        }>
        <p style={{ marginTop: 0, color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          Every row already carries its name and its dates. These are the details the
          competition itself knows — tick the ones your club puts on its sheet, and they are
          written into the note column for you. You can still edit any of them below.
        </p>
        <div className="season-field-ticks">
          {NOTE_FIELDS.map(([key, label]) => (
            <button type="button" key={key}
              className={`event-tick${include[key] ? ' event-tick-on' : ''}`}
              aria-pressed={!!include[key]} onClick={() => toggleField(key)}>
              <span className="event-tick-box" aria-hidden="true">{include[key] ? '✓' : ''}</span>
              <span className="event-tick-name">{label}</span>
            </button>
          ))}
          {/* Events have no format or bowls, so "where" is theirs alone. */}
          <button type="button" className={`event-tick${include.where ? ' event-tick-on' : ''}`}
            aria-pressed={!!include.where} onClick={() => toggleField('where')}>
            <span className="event-tick-box" aria-hidden="true">{include.where ? '✓' : ''}</span>
            <span className="event-tick-name">Where <span className="event-flag">events</span></span>
          </button>
        </div>
      </Section>

      <Section label="Heading, dates and notes">
        <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
          <Field label="Title" htmlFor="season-title" help="Printed across the top.">
            <input id="season-title" className="w3-input" value={title}
              onChange={e => setTitle(e.target.value)} />
          </Field>
          <Field label="Note at the foot" htmlFor="season-foot" help="Leave blank for none.">
            <input id="season-foot" className="w3-input" value={footNote}
              onChange={e => setFootNote(e.target.value)} />
          </Field>
        </div>

        {/* The part that is not in the database, and the reason a club's own
            sheet says more than a list of dates can. */}
        {rowCount > 0 && (
          <div style={{ marginTop: 'var(--gs-space-4)' }}>
            {sections.map(s => s.rows.map(r => {
              const dateEdited = dates[r.id] !== undefined && dates[r.id] !== r.generatedWhen;
              return (
                <div key={r.id} className="season-row-edit">
                  <div className="season-row-edit-name">{r.name}</div>
                  <div className="gs-field-grid" style={{ '--gs-cols': '1fr 1fr' }}>
                    <Field label="Dates" htmlFor={`when-${r.id}`}
                      help={dateEdited
                        ? (
                          <>
                            {`Worked out as “${r.generatedWhen}”. `}
                            <button type="button" className="season-row-undo"
                              onClick={() => setDates(prev => {
                                const next = { ...prev };
                                delete next[r.id];
                                return next;
                              })}>
                              Put it back
                            </button>
                          </>
                        )
                        : undefined}>
                      <input id={`when-${r.id}`} className="w3-input" value={r.when || ''}
                        placeholder="To be confirmed"
                        onChange={ev => setDates(prev => ({ ...prev, [r.id]: ev.target.value }))} />
                    </Field>
                    <Field label="Note" htmlFor={`note-${r.id}`}>
                      <input id={`note-${r.id}`} className="w3-input" value={r.note || ''}
                        placeholder="Nothing extra"
                        onChange={ev => setNotes(prev => ({ ...prev, [r.id]: ev.target.value }))} />
                    </Field>
                  </div>
                </div>
              );
            }))}
            <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
              Dates are worked out from the competition’s own rounds and the event’s own diary —
              type over one where your club says it differently, or where the sheet on the wall has
              to say something the record cannot. The note goes beside the row: “4 qualifying games,
              semi and final”, “max 3 games per day”. Both are kept on the sheet, not written back
              to the competition.
            </p>
            {editedDateCount > 0 && (
              <p className="poster-note" style={{ marginTop: 'var(--gs-space-2)' }}>
                {`${editedDateCount} date${editedDateCount === 1 ? '' : 's'} edited by hand. `}
                <button type="button" className="season-row-undo" onClick={() => setDates({})}>
                  Put them all back
                </button>
              </p>
            )}
          </div>
        )}
      </Section>

      <Section label="The sheet">
        {/* The canvas is the real thing; the img is what is shown, so the
            preview scales without redrawing at screen resolution. */}
        <canvas ref={canvasRef} style={{ display: 'none' }} />
        <img ref={previewRef} alt={`${title} — preview`} className="season-sheet-preview" />
      </Section>

      {error && <div className="poster-error" role="alert">{error}</div>}

      {/* Where the Make the poster button sits on every other brief, doing
          the same job: this is the end of the form. */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 'var(--gs-space-4)',
        marginTop: 'var(--gs-space-4)', flexWrap: 'wrap',
      }}>
        <button type="button" className="w3-button w3-round poster-btn-primary"
          disabled={busy || parentBusy || !rowCount} onClick={save}>
          {busy ? 'Saving…' : 'Keep this sheet'}
        </button>
        {/* Print and download need nothing saved: the sheet exists on the
            canvas already, and pinning one up is the commonest thing a club
            will do with it. */}
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={!rowCount} onClick={print}>Print</button>
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={!rowCount} onClick={download}>Download</button>
        <button type="button" className="w3-button w3-round poster-btn"
          disabled={!canGenerate || busy || drawing || !rowCount} onClick={jazz}>
          {drawing ? 'Drawing…' : backdrop ? 'Draw different artwork' : 'Jazz it up with AI'}
        </button>
        {backdrop && (
          <button type="button" className="w3-button w3-round poster-btn"
            disabled={drawing} onClick={() => setBackdrop(null)}>Plain again</button>
        )}
        <span style={{ color: 'var(--gs-text-muted)', fontSize: 'var(--gs-text-sm)' }}>
          {parentBusy
            ? 'This takes up to a minute.'
            : !rowCount
              ? 'Tick a competition or an event.'
              : 'Keeping it puts the sheet with your posters, where the club screen and the newsletter can use it. Print and Download need nothing saved. Jazzing it up sends this exact sheet to be redrawn as a designed poster, and costs one generation.'}
        </span>
      </div>
    </>
  );
}

export default SeasonSheet;
